import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { SchoolOrdersModal } from "@/components/storefront/SchoolOrdersModal";
import { ArrowRight, Phone, MessageCircle, GraduationCap, PenTool, BookMarked, Music, Cpu, Church, ShoppingBasket, ClipboardList, Truck } from "lucide-react";

const catIcons = {
  "educational-books": GraduationCap,
  stationery: PenTool,
  bibles: BookMarked,
  "christian-books": Church,
  "musical-instruments": Music,
  "computer-accessories": Cpu,
};

export default function Home() {
  const { settings } = useStore();
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get("/categories").then((r) => r.data),
  });
  const { data: featured = [] } = useQuery({
    queryKey: ["featured"],
    queryFn: () => api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => r.data),
  });
  const { data: schools = [] } = useQuery({
    queryKey: ["schools"],
    queryFn: () => api.get("/schools").then((r) => r.data),
  });
  const [schoolOpen, setSchoolOpen] = useState(false);

  const whatsappNumber = (settings?.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-brand-cream border-b-2 border-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span className="inline-block bg-brand-gold text-black text-sm font-bold px-3 py-1 border-2 border-black rounded-md shadow-brutal mb-6">
              📍 Based in Abeokuta, Nigeria
            </span>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none text-brand-blue">
              Books, stationery
              <br />
              <span className="text-brand-black">& so much </span>
              <span className="relative inline-block">
                more
                <span className="absolute left-0 -bottom-1 w-full h-3 bg-brand-gold -z-0" />
              </span>
            </h1>
            <p className="mt-6 text-lg text-brand-black/70 max-w-xl leading-relaxed">
              {settings?.tagline ||
                "Everything students, families and churches need — educational books, stationery, bibles, christian literature, musical instruments and computer accessories."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg active:translate-y-0.5 transition-all duration-200"
              >
                Browse the shop <ArrowRight className="w-5 h-5" />
              </Link>
              {settings?.phone && (
                <a
                  href={`tel:${settings.phone}`}
                  data-testid="hero-call-btn"
                  className="inline-flex items-center gap-2 bg-brand-gold text-black font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 hover:shadow-brutal-lg active:translate-y-0.5 transition-all duration-200"
                >
                  <Phone className="w-5 h-5" /> Call to order
                </a>
              )}
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative border-2 border-black rounded-lg overflow-hidden shadow-brutal-lg rotate-1">
              <img
                src="https://images.unsplash.com/photo-1762350834823-8b8cab219191?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB2aWJyYW50JTIwYm9va3N0b3JlJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg2MzgxNDQzfDA&ixlib=rb-4.1.0&q=85&w=900"
                alt="BooksNmore bookstore"
                className="w-full h-[420px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-brand-blue">Shop by category</h2>
            <p className="mt-2 text-brand-black/60">Find exactly what you need, faster.</p>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-1 font-semibold text-brand-blue hover:text-brand-gold" data-testid="view-all-categories">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
          {categories.map((c, i) => {
            const Icon = catIcons[c.slug] || BookMarked;
            const big = i === 0;
            return (
              <Link
                key={c.id}
                to={`/shop?category=${c.slug}`}
                data-testid={`category-card-${c.slug}`}
                className={`group relative overflow-hidden border-2 border-black rounded-lg shadow-brutal hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200 ${
                  big ? "col-span-2 lg:row-span-2 min-h-[220px] lg:min-h-[420px]" : "min-h-[180px]"
                }`}
              >
                {c.image && (
                  <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue via-brand-blue/40 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-5">
                  <span className="w-10 h-10 mb-3 flex items-center justify-center bg-brand-gold border-2 border-black rounded-md">
                    <Icon className="w-5 h-5 text-brand-blue" strokeWidth={2.5} />
                  </span>
                  <h3 className="font-display font-bold text-white text-lg leading-tight">{c.name}</h3>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured products */}
      {featured.length > 0 && (
        <section className="bg-brand-cream border-y-2 border-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
            <div className="flex items-end justify-between mb-10">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-brand-blue">Featured picks</h2>
              <Link to="/shop" className="inline-flex items-center gap-1 font-semibold text-brand-blue hover:text-brand-gold" data-testid="view-all-products">
                See all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {featured.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How to order */}
      <section className="border-t-2 border-black" data-testid="how-to-order">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="max-w-2xl">
            <span className="inline-block bg-brand-gold text-black text-sm font-bold px-3 py-1 border-2 border-black rounded-md shadow-brutal mb-4">
              New — Send us your shopping list
            </span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-brand-blue">Order in 3 easy steps</h2>
            <p className="mt-2 text-brand-black/60">Build a list online and send it to us — no account needed. We'll call to confirm payment &amp; delivery.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              { icon: ShoppingBasket, t: "Add to your list", d: "Browse the shop and tap \u201CAdd to list\u201D on anything you need." },
              { icon: ClipboardList, t: "Add your details", d: "Open your list and enter your name, phone and address." },
              { icon: Truck, t: "We confirm & deliver", d: "Send it over — we'll call you to arrange payment and delivery." },
            ].map((s, i) => (
              <div key={i} className="relative bg-white border-2 border-black rounded-lg p-6 shadow-brutal hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200">
                <span className="absolute -top-3 -left-3 w-9 h-9 flex items-center justify-center bg-brand-blue text-white font-display font-black border-2 border-black rounded-full">{i + 1}</span>
                <span className="w-11 h-11 flex items-center justify-center bg-brand-gold border-2 border-black rounded-md mb-4">
                  <s.icon className="w-5 h-5 text-brand-blue" strokeWidth={2.5} />
                </span>
                <h3 className="font-display font-bold text-lg text-brand-black">{s.t}</h3>
                <p className="mt-2 text-sm text-brand-black/60 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <Link
            to="/shop"
            data-testid="how-start-list"
            className="mt-10 inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 active:translate-y-0.5 transition-all"
          >
            <ShoppingBasket className="w-5 h-5" /> Start your shopping list <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* School Orders */}
      {schools.length > 0 && (
        <section className="bg-brand-blue border-y-2 border-black" data-testid="school-orders-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
            <div className="max-w-2xl">
              <span className="inline-block bg-brand-gold text-black text-sm font-bold px-3 py-1 border-2 border-black rounded-md shadow-brutal-black mb-4">
                For schools &amp; parents
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">School Orders made easy</h2>
              <p className="mt-2 text-white/70">Pick your school and class — we'll build the exact booklist for you in seconds.</p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              {schools.slice(0, 12).map((s) => (
                <button
                  key={s.slug}
                  onClick={() => setSchoolOpen(true)}
                  data-testid={`home-school-${s.slug}`}
                  className="group bg-white border-2 border-black rounded-lg p-4 w-32 shadow-brutal hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200"
                >
                  {s.logo ? (
                    <img src={s.logo} alt={s.name} className="w-16 h-16 mx-auto object-contain" />
                  ) : (
                    <span className="w-16 h-16 mx-auto flex items-center justify-center bg-brand-cream border-2 border-black rounded-md">
                      <GraduationCap className="w-7 h-7 text-brand-blue" strokeWidth={2.5} />
                    </span>
                  )}
                  <p className="mt-2 text-xs font-semibold text-brand-black text-center">{s.name}</p>
                </button>
              ))}
            </div>
            <button
              onClick={() => setSchoolOpen(true)}
              data-testid="home-school-orders-btn"
              className="mt-10 inline-flex items-center gap-2 bg-brand-gold text-black font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal-black hover:-translate-y-0.5 active:translate-y-0.5 transition-all"
            >
              <GraduationCap className="w-5 h-5" /> Start a school order <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>
      )}

      {/* Order CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="bg-brand-blue border-2 border-black rounded-lg shadow-brutal-lg p-8 md:p-14 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-black text-white">Ready to order?</h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">
            Call us or send a WhatsApp message with the items you need — we'll sort out payment and delivery for you.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/shop" data-testid="cta-start-list" className="inline-flex items-center gap-2 bg-brand-gold text-black font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 transition-all">
              <ShoppingBasket className="w-5 h-5" /> Start a shopping list
            </Link>
            {settings?.phone && (
              <a href={`tel:${settings.phone}`} data-testid="cta-call-btn" className="inline-flex items-center gap-2 bg-brand-gold text-black font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal hover:-translate-y-0.5 transition-all">
                <Phone className="w-5 h-5" /> {settings.phone}
              </a>
            )}
            {whatsappNumber && (
              <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp-btn" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 border-2 border-black rounded-md shadow-brutal-black hover:-translate-y-0.5 transition-all">
                <MessageCircle className="w-5 h-5" /> WhatsApp us
              </a>
            )}
          </div>
        </div>
      </section>
      <SchoolOrdersModal open={schoolOpen} onOpenChange={setSchoolOpen} />
    </div>
  );
}
